import { type MouseEvent, useSyncExternalStore } from 'react';

/** 서비스 전체 보기. 주소에는 빈 `service`를 남기지 않는다. */
export const ALL_SERVICES = '';

const CHANGE_EVENT = 'apm-service-filter-change';

function subscribe(listener: () => void): () => void {
  window.addEventListener('popstate', listener);
  window.addEventListener(CHANGE_EVENT, listener);
  return () => {
    window.removeEventListener('popstate', listener);
    window.removeEventListener(CHANGE_EVENT, listener);
  };
}

/** 해시 밖의 주소 조건을 읽는다. `/console?service=a#/live`에서 `a`다. */
export function currentServiceFilter(): string {
  return new URLSearchParams(window.location.search).get('service') ?? ALL_SERVICES;
}

function serverServiceFilter(): string {
  return ALL_SERVICES;
}

/**
 * 주소가 주인인 전역 서비스 조건.
 *
 * 해시를 건드리지 않으므로 메뉴·상세를 오가도 유지되고, 주소를 복사하면 선택도 함께 간다.
 */
export function useServiceFilter(): string {
  return useSyncExternalStore(subscribe, currentServiceFilter, serverServiceFilter);
}

/**
 * 27회차 전 링크는 서비스 조건을 해시 쿼리에 실었다. 부팅 전에 그 값을 전역 자리로 한 번 옮긴다.
 * 검색 조건은 그대로 두고 `replaceState`로 바꿔 뒤로 가기 이력을 늘리지 않는다.
 */
export function promoteLegacyServiceFilter(): string | undefined {
  if (currentServiceFilter() !== ALL_SERVICES) return undefined;
  const url = new URL(window.location.href);
  const queryAt = url.hash.indexOf('?');
  if (queryAt < 0) return undefined;

  const params = new URLSearchParams(url.hash.slice(queryAt + 1));
  const service = params.get('service');
  if (service === null || service === '') return undefined;

  params.delete('service');
  url.searchParams.set('service', service);
  const query = params.toString();
  url.hash = `${url.hash.slice(0, queryAt)}${query === '' ? '' : `?${query}`}`;
  window.history.replaceState(window.history.state, '', url);
  return service;
}

/** 서비스 선택과 해시 경로를 모두 담은 복사 가능한 내부 링크. */
export function serviceFilterHref(service: string, hash: string): string {
  const url = new URL(window.location.href);
  if (service === ALL_SERVICES) url.searchParams.delete('service');
  else url.searchParams.set('service', service);
  url.hash = hash;
  return `${url.pathname}${url.search}${url.hash}`;
}

/** 현재 화면을 둔 채 `?service=`만 바꾼다. 빈 값은 전체 보기라 키를 지운다. */
export function setServiceFilter(service: string, replace = false): void {
  const url = new URL(window.location.href);
  const current = currentServiceFilter();
  if (service === ALL_SERVICES) url.searchParams.delete('service');
  else url.searchParams.set('service', service);
  if (service === current) return;

  if (replace) window.history.replaceState(null, '', url);
  else window.history.pushState(null, '', url);
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/**
 * 서비스 선택과 화면을 한 번에 옮긴다 (44회차).
 *
 * 선택을 먼저 바꾸고 해시를 따로 옮기면 이력이 두 칸 쌓여 뒤로 가기 한 번으로 못 돌아가고,
 * 그 사이 지금 화면이 새 서비스로 한 번 더 조회한다. 주소를 한 번 쓰고 두 구독자를 함께 깨운다
 */
export function navigateWithService(service: string, hash: string): void {
  const href = serviceFilterHref(service, hash);
  const { pathname, search, hash: currentHash } = window.location;
  if (href === `${pathname}${search}${currentHash}`) return;
  window.history.pushState(null, '', href);
  window.dispatchEvent(new Event(CHANGE_EVENT));
  window.dispatchEvent(new HashChangeEvent('hashchange'));
}

/**
 * 서비스를 함께 옮기는 링크의 `href`와 누르기 처리 (44회차). 서비스가 정해지지 않은 링크는 해시만
 * 쓴다. `href`에 서비스가 실려 있어 새 탭으로 열어도(가운데 단추·Ctrl·Shift) 같은 화면이 뜬다
 */
export function serviceLink(
  service: string | undefined,
  hash: string,
): { href: string; onClick?: (event: MouseEvent<HTMLAnchorElement>) => void } {
  if (service === undefined) return { href: hash };
  return {
    href: serviceFilterHref(service, hash),
    onClick: (event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      event.preventDefault();
      navigateWithService(service, hash);
    },
  };
}
